<template>
  <SDialog v-model:open="modalVisible">
    <SDialogContent
      class="fiscal-reports !w-[640px] !max-w-[640px]"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle>
          <h2>{{ $t("compliance.fiscal-reports") }}</h2>
        </SDialogTitle>
        <SSeparator class="mb-0" />
      </SDialogHeader>

      <div>
        <template v-for="(report, index) in reports" :key="report.key">
          <SSeparator v-if="index" />
          <div class="flex items-center justify-between gap-4 py-3">
            <p class="font-semibold">{{ reportLabel(report) }}</p>

            <div class="flex items-center gap-3">
              <!-- Fixed width so every Print button lands on the same column,
                   including the X rows, which cover the open period and so
                   take no date at all. -->
              <div class="w-[150px]">
                <!-- Simulations always run for today: the date is shown for
                     confirmation, not for choosing. -->
                <SButton
                  v-if="report.kind === 'simulation'"
                  type="button"
                  variant="outline"
                  disabled
                  class="w-full justify-start gap-2"
                >
                  <CalendarIcon style="font-size: 18px" />
                  {{ formatDate(reportDates[report.key]) }}
                </SButton>

                <SPopover v-else-if="report.kind === 'z'" v-slot="{ close }">
                  <SPopoverTrigger as-child>
                    <SButton
                      type="button"
                      variant="outline"
                      class="w-full justify-start gap-2"
                      :disabled="isBusy"
                      :data-cy="`fiscal-report-date-${report.key}`"
                    >
                      <CalendarIcon style="font-size: 18px" />
                      {{ formatDate(reportDates[report.key]) }}
                    </SButton>
                  </SPopoverTrigger>
                  <SPopoverContent class="w-auto p-0" align="start">
                    <SCalendar
                      :model-value="reportDateValues[report.key]"
                      :is-date-unavailable="
                        (date) =>
                          isDateDisabled(date.toDate(getLocalTimeZone()))
                      "
                      initial-focus
                      @update:model-value="
                        (value) => {
                          if (value) {
                            selectDate(
                              report,
                              value.toDate(getLocalTimeZone())
                            );
                          }
                          close();
                        }
                      "
                    />
                  </SPopoverContent>
                </SPopover>
              </div>

              <SButton
                type="button"
                class="min-w-[110px] font-bold"
                :disabled="isBusy || isPrintDisabled(report)"
                :data-cy="`fiscal-report-print-${report.key}`"
                @click="printReport(report)"
              >
                <LoaderCircleIcon
                  v-if="loadingKey === report.key"
                  class="s-icon-spin"
                />
                {{ $t("completeorder.print") }}
              </SButton>
            </div>
          </div>
        </template>
      </div>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, reactive, ref } from "vue";
import dayjs from "dayjs";
import { EventType } from "@smeetz/compliance-common";
import {
  SButton,
  SCalendar,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SPopover,
  SPopoverContent,
  SPopoverTrigger,
  SSeparator,
} from "@smeetz/ui-core";
import { CalendarIcon, LoaderCircleIcon } from "@smeetz/ui-icons";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { useI18n } from "vue-i18n";

import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { processAdditionalEvents } from "@/utils/helpers/compliance";
import { base64toPdfBlob } from "@/utils/vue-helpers/badges";
import NotificationService from "@/services/notification.service";
import { LogService } from "@/services/logging";

/**
 * X reports summarise the current, still-open period and are signed on the
 * spot. Z reports are the daily closure: the compliance package generates and
 * signs them automatically when a new day's WORK_IN closes the previous
 * period, so asking for one only fetches the PDF of a report that already
 * exists. Simulations run a Z report in training mode without closing
 * anything.
 */
type ReportKind = "x" | "z" | "simulation";

interface FiscalReport {
  key: string;
  labelKey: string;
  eventType: EventType;
  kind: ReportKind;
}

const REPORTS: FiscalReport[] = [
  {
    key: "xUser",
    labelKey: "compliance.x-user-report",
    eventType: EventType.REPORT_USER_X,
    kind: "x",
  },
  {
    key: "xTurnover",
    labelKey: "compliance.x-turnover-report",
    eventType: EventType.REPORT_TURNOVER_X,
    kind: "x",
  },
  {
    key: "zUser",
    labelKey: "compliance.z-user-report",
    eventType: EventType.REPORT_USER_Z,
    kind: "z",
  },
  {
    key: "zTurnover",
    labelKey: "compliance.z-turnover-report",
    eventType: EventType.REPORT_TURNOVER_Z,
    kind: "z",
  },
  {
    key: "zUserSimulation",
    labelKey: "compliance.z-user-report",
    eventType: EventType.REPORT_USER_Z_SIMULATION,
    kind: "simulation",
  },
  {
    key: "zTurnoverSimulation",
    labelKey: "compliance.z-turnover-report",
    eventType: EventType.REPORT_TURNOVER_Z_SIMULATION,
    kind: "simulation",
  },
];

/** First day the FDM signed sales; nothing can be reported before it. */
const FDM_GO_LIVE = import.meta.env.VITE_FDM_DATE || "2025-08-21";

export default defineComponent({
  name: ModalComponents.FiscalReports,
  components: {
    SButton,
    SCalendar,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    SPopover,
    SPopoverContent,
    SPopoverTrigger,
    SSeparator,
    CalendarIcon,
    LoaderCircleIcon,
  },
  setup() {
    const { t } = useI18n({});
    const modalVisible = dialogVisibleForStatus(ModalStatus.FiscalReports);
    const loadingKey = ref<string | null>(null);
    const isBusy = computed(() => loadingKey.value !== null);

    // X reports have no date of their own — they always describe the period
    // that is still open — so only the dated kinds get an entry here.
    const yesterday = dayjs().subtract(1, "day").toDate();
    const today = dayjs().toDate();
    const reportDates = reactive<Record<string, Date>>(
      Object.fromEntries(
        REPORTS.filter((report) => report.kind !== "x").map((report) => [
          report.key,
          report.kind === "simulation" ? today : yesterday,
        ])
      )
    );

    /**
     * Kept as a computed map rather than built per render, so the calendar does
     * not reset its displayed month on every re-render (L3-7935).
     */
    const reportDateValues = computed(() =>
      Object.fromEntries(
        Object.entries(reportDates).map(([key, date]) => [
          key,
          parseDate(dayjs(date).format("YYYY-MM-DD")),
        ])
      )
    );

    /**
     * Today and the future are unavailable because a Z report only exists once
     * its booking period has closed, which happens on the next day's login.
     */
    function isDateDisabled(date: Date) {
      const selected = dayjs(date).startOf("day");
      return (
        !selected.isBefore(dayjs().startOf("day")) ||
        selected.isBefore(dayjs(FDM_GO_LIVE).startOf("day"))
      );
    }

    function isPrintDisabled(report: FiscalReport) {
      return report.kind === "z" && isDateDisabled(reportDates[report.key]);
    }

    function selectDate(report: FiscalReport, date: Date) {
      reportDates[report.key] = date;
    }

    function formatDate(date: Date) {
      return dayjs(date).format("DD MMM YYYY");
    }

    function reportLabel(report: FiscalReport) {
      const name = t(report.labelKey);
      return report.kind === "simulation"
        ? `${name} (${t("compliance.simulation")})`
        : name;
    }

    // Revoked on unmount rather than straight after window.open, which would
    // race the tab that is still loading the PDF.
    const blobUrls: string[] = [];
    onBeforeUnmount(() => blobUrls.forEach((url) => URL.revokeObjectURL(url)));

    function openPdf(pdfBase64: string) {
      const url = URL.createObjectURL(base64toPdfBlob(pdfBase64));
      blobUrls.push(url);
      window.open(url, `fileDownload_${Date.now()}`);
    }

    async function printReport(report: FiscalReport) {
      const label = reportLabel(report);
      loadingKey.value = report.key;
      try {
        const additionalData: Record<string, string> = {};
        if (report.kind === "z") {
          additionalData.reportDate = dayjs(reportDates[report.key]).format(
            "YYYY-MM-DD"
          );
        } else if (report.kind === "simulation") {
          // Both simulation handlers reject a request with no date, so today's
          // is sent explicitly rather than left to the package.
          additionalData.reportDate = dayjs().format("YYYY-MM-DD");
        }

        const result = await processAdditionalEvents(
          report.eventType,
          additionalData
        );
        LogService.info("Compliance: fiscal report generated", {
          report: report.key,
          result,
        });

        const pdfBase64 = result?.data?.pdfBase64;
        if (!pdfBase64) {
          NotificationService.showCustomError(
            `${t("compliance.no-pdf-data-for")} ${label}.`
          );
          return;
        }
        openPdf(pdfBase64);
        NotificationService.showCustomSuccess(
          `${label} ${t("compliance.printed-successfully")}!`
        );
      } catch (error) {
        LogService.info("Compliance: error generating a fiscal report", {
          report: report.key,
          error,
        });
        NotificationService.showGeneralResponseError();
      } finally {
        loadingKey.value = null;
      }
    }

    return {
      modalVisible,
      reports: REPORTS,
      reportDates,
      reportDateValues,
      loadingKey,
      isBusy,
      isDateDisabled,
      isPrintDisabled,
      selectDate,
      formatDate,
      reportLabel,
      printReport,
      getLocalTimeZone,
    };
  },
});
</script>
